import { socket } from '../core/socket.js';
import { state } from '../core/state.js';
import { convertirRaccourcisEnEmojis } from '../utils/formatters.js';
import { getUserEmojis, getSphereEmojis } from '../core/customEmojis.js';

const TWEMOJI_OPTS = {
    folder: 'svg',
    ext: '.svg',
    base: 'https://cdn.jsdelivr.net/gh/jdecked/twemoji@15/assets/',
    className: 'twemoji',
};

// ── Helpers contenteditable ───────────────────────────────────────────────

/**
 * Sérialise le contenu de l'input en texte brut pour l'envoi.
 * img.chat-emoji-custom → ":nom:", img.twemoji → alt (char unicode), br/div → \n
 */
export function _getInputText(el) {
    let text = '';
    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent;
        } else if (node.nodeName === 'IMG') {
            if (node.classList.contains('chat-emoji-custom')) {
                text += ':' + node.dataset.nom + ':';
            } else if (node.classList.contains('twemoji')) {
                text += node.alt || '';
            }
        } else if (node.nodeName === 'BR') {
            text += '\n';
        } else if (node.nodeName === 'DIV' && text.length > 0) {
            text += '\n';
            node.childNodes.forEach(walk);
        } else {
            node.childNodes.forEach(walk);
        }
    };
    el.childNodes.forEach(walk);
    return text;
}

/** Vide l'input contenteditable */
export function _clearInput(el) {
    if (!el) return;
    el.innerHTML = '';
    el.dispatchEvent(new Event('input'));
}

/** Texte brut avant le curseur (pour autocomplete `:emoji` et `@mention`) */
export function _textBeforeCursor(el) {
    const sel = window.getSelection();
    if (!sel.rangeCount) return '';
    const range = sel.getRangeAt(0).cloneRange();
    try {
        range.setStart(el, 0);
    } catch (e) {
        return '';
    }
    const fragment = range.cloneContents();
    const tmp = document.createElement('div');
    tmp.appendChild(fragment);
    return _getInputText(tmp);
}

/** Insère un nœud DOM à la position du curseur */
export function _insertNodeAtCursor(node) {
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
}

/** Insère du texte à la position du curseur */
export function _insertTextAtCursor(text) {
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
}

/**
 * Applique Twemoji sur le contenteditable en préservant la position du curseur.
 * Technique : insertion d'un <span> marqueur, parse, restauration après le marqueur.
 */
export function _applyTwemojiInput(el) {
    if (!el || !window.twemoji) return;

    // Vérification rapide : y a-t-il des emojis unicode dans les nœuds texte ?
    let hasEmoji = false;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
        if (/\p{Emoji_Presentation}|\p{Extended_Pictographic}/u.test(walker.currentNode.textContent)) {
            hasEmoji = true;
            break;
        }
    }
    if (!hasEmoji) return;

    const sel = window.getSelection();
    const hasFocus = el.contains(sel.anchorNode);

    // Insérer un marqueur à la position du curseur
    let marker = null;
    if (hasFocus && sel.rangeCount) {
        marker = document.createElement('span');
        marker.id = '_ce_cursor_marker';
        try {
            sel.getRangeAt(0).insertNode(marker);
        } catch (e) {
            marker = null;
        }
    }

    window.twemoji.parse(el, TWEMOJI_OPTS);

    // Restaurer le curseur après le marqueur
    if (marker) {
        const m = el.querySelector('#_ce_cursor_marker');
        if (m) {
            const r = document.createRange();
            r.setStartAfter(m);
            r.collapse(true);
            sel.removeAllRanges();
            sel.addRange(r);
            m.remove();
        }
    }
}

// ── Icône micro / envoi ───────────────────────────────────────────────────

export function _majSaisie() {
    const inp = document.getElementById('input');
    if (!inp) return;
    const bar = inp.closest('.input-area');
    if (bar) bar.classList.toggle('is-composing', _getInputText(inp).trim().length > 0);
}

// ── Autocomplete emojis (:nom) ────────────────────────────────────────────

let _emojiFlyoutIndex = -1;

function _detecterEmojiAuto() {
    const input = document.getElementById('input');
    const flyout = document.getElementById('emoji-flyout');
    if (!input || !flyout) return;

    const avant = _textBeforeCursor(input);
    const match = avant.match(/:([a-z0-9_]{1,32})$/);
    if (!match) { _fermerEmojiFlyout(); return; }

    const query = match[1];
    const sphereId = state.sphereActuelleID;
    const custom = [
        ...getUserEmojis(),
        ...(sphereId ? getSphereEmojis(sphereId) : [])
    ];
    const seen = new Set();
    const pool = custom.filter(e => { if (seen.has(e.nom)) return false; seen.add(e.nom); return true; });
    const resultats = pool.filter(e => e.nom.startsWith(query)).slice(0, 10);

    if (!resultats.length) { _fermerEmojiFlyout(); return; }

    _emojiFlyoutIndex = -1;
    flyout.innerHTML = resultats.map((e, i) =>
        `<div class="emoji-flyout-item" data-nom="${e.nom}" data-i="${i}" role="option">
            <img src="${e.url}" alt=":${e.nom}:" loading="lazy">
            <span>:${e.nom}:</span>
        </div>`
    ).join('');
    flyout.style.display = 'block';

    flyout.querySelectorAll('.emoji-flyout-item').forEach(item => {
        item.addEventListener('mousedown', e => { e.preventDefault(); _choisirEmojiAuto(item.dataset.nom); });
        item.addEventListener('mouseover', () => {
            _emojiFlyoutIndex = parseInt(item.dataset.i);
            _majSelEmojiAuto();
        });
    });
}

function _fermerEmojiFlyout() {
    const flyout = document.getElementById('emoji-flyout');
    if (flyout) flyout.style.display = 'none';
    _emojiFlyoutIndex = -1;
}

function _choisirEmojiAuto(nom) {
    const input = document.getElementById('input');
    if (!input) return;

    const avant = _textBeforeCursor(input);
    const match = avant.match(/:([a-z0-9_]{1,32})$/);
    if (!match) return;

    const tokenLen = match[0].length; // longueur de ":query"

    // Supprimer le token ":query" avant le curseur
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    sel.collapseToEnd();
    for (let i = 0; i < tokenLen; i++) {
        sel.modify('extend', 'backward', 'character');
    }
    const range = sel.getRangeAt(0);
    range.deleteContents();

    // Trouver l'emoji custom et insérer son <img>
    const sphereId = state.sphereActuelleID;
    const custom = [...getUserEmojis(), ...(sphereId ? getSphereEmojis(sphereId) : [])];
    const emoji = custom.find(e => e.nom === nom);
    if (emoji) {
        const img = document.createElement('img');
        img.src = emoji.url;
        img.alt = `:${nom}:`;
        img.dataset.nom = nom;
        img.className = 'chat-emoji-custom';
        // Insérer l'img + espace
        const space = document.createTextNode(' ');
        range.insertNode(space);
        range.insertNode(img);
        range.setStartAfter(space);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    }

    input.focus();
    if (typeof window._majSaisie === 'function') window._majSaisie();
    _fermerEmojiFlyout();
}

function _majSelEmojiAuto() {
    const flyout = document.getElementById('emoji-flyout');
    if (!flyout) return;
    flyout.querySelectorAll('.emoji-flyout-item').forEach((item, i) => {
        item.classList.toggle('active', i === _emojiFlyoutIndex);
    });
    const active = flyout.querySelector('.emoji-flyout-item.active');
    if (active) active.scrollIntoView({ block: 'nearest' });
}

function _emojiAutocompleteKeydown(e) {
    const flyout = document.getElementById('emoji-flyout');
    if (!flyout || flyout.style.display === 'none') return false;
    const items = flyout.querySelectorAll('.emoji-flyout-item');
    if (!items.length) return false;
    if (e.key === 'ArrowDown') {
        e.preventDefault();
        _emojiFlyoutIndex = Math.min(_emojiFlyoutIndex + 1, items.length - 1);
        _majSelEmojiAuto();
        return true;
    }
    if (e.key === 'ArrowUp') {
        e.preventDefault();
        _emojiFlyoutIndex = Math.max(_emojiFlyoutIndex - 1, 0);
        _majSelEmojiAuto();
        return true;
    }
    if ((e.key === 'Enter' || e.key === 'Tab') && _emojiFlyoutIndex >= 0) {
        e.preventDefault();
        const nom = items[_emojiFlyoutIndex]?.dataset.nom;
        if (nom) _choisirEmojiAuto(nom);
        return true;
    }
    if (e.key === 'Escape') { _fermerEmojiFlyout(); return true; }
    return false;
}

// ── Init ──────────────────────────────────────────────────────────────────

export function initChatInput() {
    const input = document.getElementById('input');
    if (!input) return;

    // Limite de caractères (maxlength n'existe pas sur contenteditable)
    const CHAR_LIMIT = 1000;

    // 1. Touches : Entrée envoie, Shift+Entrée = saut de ligne, ↑ édite dernier message
    input.addEventListener('keydown', (e) => {
        if (_emojiAutocompleteKeydown(e)) return;
        if (window._mentionFlyoutKeydown && window._mentionFlyoutKeydown(e)) return;

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (typeof window.send === 'function') window.send();
            return;
        }

        // ↑ vide → éditer le dernier message de l'utilisateur
        if (e.key === 'ArrowUp' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
            if (_getInputText(input).trim() === '') {
                const monPseudo = window.monPseudo || window.state?.monPseudo;
                if (!monPseudo) return;
                const items = document.querySelectorAll(`#messages .message-item[data-auteur="${CSS.escape(monPseudo)}"][data-msg-id]`);
                const last = items[items.length - 1];
                if (last && last.dataset.msgId && last.dataset.texte !== undefined) {
                    e.preventDefault();
                    if (typeof window.commencerEdition === 'function') {
                        window.commencerEdition({ id: last.dataset.msgId, texte: last.dataset.texte });
                    }
                }
            }
        }

        // Bloquer la saisie au-delà de CHAR_LIMIT
        if (!e.ctrlKey && !e.metaKey && e.key.length === 1) {
            if (_getInputText(input).length >= CHAR_LIMIT) {
                e.preventDefault();
            }
        }
    });

    // 2. Input : twemoji, autocomplete emoji/mention, icône, compteur
    input.addEventListener('input', () => {
        _applyTwemojiInput(input);
        if (typeof window._detecterMention === 'function') window._detecterMention();
        _detecterEmojiAuto();
        _majSaisie();
        _majCompteur(input, CHAR_LIMIT);
    });

    input.addEventListener('click', () => {
        if (typeof window._detecterMention === 'function') window._detecterMention();
        _detecterEmojiAuto();
    });

    input.addEventListener('blur', () => {
        if (typeof window._fermerMentionFlyout === 'function') {
            setTimeout(window._fermerMentionFlyout, 120);
        }
        setTimeout(_fermerEmojiFlyout, 120);
        _majSaisie();
    });

    // 3. Paste : ne coller que du texte brut
    input.addEventListener('paste', (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        if (!text) return;
        // Limiter si on dépasse
        const current = _getInputText(input);
        const available = Math.max(0, CHAR_LIMIT - current.length);
        const toInsert = text.slice(0, available);
        if (!toInsert) return;
        document.execCommand('insertText', false, toInsert);
        _applyTwemojiInput(input);
        _majCompteur(input, CHAR_LIMIT);
    });

    // 4. Branchement du champ de recherche GIF
    const gifInput = document.getElementById('gif-search-input');
    if (gifInput) {
        gifInput.addEventListener('input', searchGifs);
    }
}

function _majCompteur(input, limit) {
    const counter = document.getElementById('char-counter');
    if (!counter) return;
    const len = _getInputText(input).length;
    const restant = limit - len;
    if (len >= 900) {
        counter.style.display = 'block';
        counter.style.position = 'absolute';
        counter.style.right = '85px';
        counter.style.bottom = '5px';
        counter.style.zIndex = '10';
        counter.textContent = `-${restant}`;
        counter.style.color = restant <= 25 ? '#f85149' : '#8b949e';
    } else {
        counter.style.display = 'none';
    }
}

export async function searchGifs() {
    const inputEl = document.getElementById('gif-search-input');
    if (!inputEl) return;
    const query = inputEl.value;
    if (query.length < 2) return;

    const url = `/api/giphy?q=${encodeURIComponent(query)}&limit=50&rating=g`;
    try {
        const response = await fetch(url);
        const data = await response.json();
        const resultsDiv = document.getElementById('gif-results');
        if (!resultsDiv) return;
        resultsDiv.innerHTML = '';

        data.data.forEach(gif => {
            const img = document.createElement('img');
            img.src = gif.images.fixed_height_small.url;
            img.onclick = async () => {
                const gifUrl = gif.images.original.url;
                const dest = window.destinataireID || state.destinataireID;
                const e2ee = dest && window.ChaosMPCrypto
                    && await window.ChaosKeystore.getMeta('device_id')
                    && typeof window.envoyerMPE2EE === 'function';
                if (e2ee) {
                    await window.envoyerMPE2EE(gifUrl);
                } else {
                    socket.emit('chat message', {
                        auteur: window.monPseudo || state.monPseudo,
                        texte: gifUrl,
                        destinataire: dest || 'public',
                        salonId: window.salonActuelID || state.salonActuelID,
                        salon_id: window.salonActuelID || state.salonActuelID
                    });
                }
                inputEl.value = '';
                if (typeof window.toggleGifPicker === 'function') window.toggleGifPicker();
            };
            resultsDiv.appendChild(img);
        });
    } catch (err) {
        console.error('Erreur Giphy :', err);
    }
}

customElements.whenDefined('emoji-picker').then(() => {
    const picker = document.querySelector('emoji-picker');
    if (picker && picker.shadowRoot) {
        const shadowStyle = document.createElement('style');
        shadowStyle.textContent = `
            ::-webkit-scrollbar { width: 8px; height: 8px; }
            ::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.2); border-radius: 4px; }
            ::-webkit-scrollbar-thumb { background: var(--theme-primary); border-radius: 4px; }
            ::-webkit-scrollbar-thumb:hover { filter: brightness(1.2); }
            input.search { color: var(--text-main) !important; }
        `;
        picker.shadowRoot.appendChild(shadowStyle);
    }
});
